import { computed } from "vue";
import { useQueryManager } from "@/composables/useQueryManager";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { ChatLogFilters, ChatOrder } from "./tradeChatLogsTypes";

const dayParam = (value?: string): string | null =>
  value &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  Number.isFinite(Date.parse(value))
    ? value
    : null;
const positiveId = (value?: string): number | null =>
  value &&
  /^\d+$/.test(value) &&
  Number.isSafeInteger(Number(value)) &&
  Number(value) > 0
    ? Number(value)
    : null;

/** Keeps search filters and the open message in the URL, so every view is a shareable link. */
export function useTradeChatLogQuery() {
  const { getQueryValue, updateQuery, parsers } = useQueryManager();
  const { globalErrorHandler } = useGlobalHandlers();
  const q = getQueryValue("q", parsers.string);
  const conversation = getQueryValue("conversation", parsers.string);
  const sender = getQueryValue("sender", parsers.string);
  const since = getQueryValue("since", (value) => dayParam(value) ?? undefined);
  const until = getQueryValue("until", (value) => dayParam(value) ?? undefined);
  const signals = getQueryValue(
    "signals",
    parsers.enum<"with" | "without">(["with", "without"])
  );
  const order = getQueryValue(
    "order",
    parsers.enum<ChatOrder>(["newest", "oldest"])
  );
  const message = getQueryValue(
    "message",
    (value) => positiveId(value) ?? undefined
  );

  const navigate = (updates: Record<string, string | undefined>) =>
    updateQuery(updates).catch(globalErrorHandler);

  const filters = computed<ChatLogFilters>({
    get: () => ({
      q: q.value ?? "",
      conversationId: conversation.value || null,
      sender: sender.value ?? "",
      since: since.value ?? null,
      until: until.value ?? null,
      signals: signals.value ?? null,
      order: order.value ?? "newest",
    }),
    set: (value) => {
      void navigate({
        q: value.q.trim() || undefined,
        conversation: value.conversationId ?? undefined,
        sender: value.sender.trim() || undefined,
        since: value.since ?? undefined,
        until: value.until ?? undefined,
        signals: value.signals ?? undefined,
        order: value.order === "newest" ? undefined : value.order,
      });
    },
  });
  const selectedMessageId = computed(() => message.value ?? null);

  function selectMessage(id: number | null) {
    void navigate({ message: id?.toString() });
  }

  return { filters, selectedMessageId, selectMessage };
}
